import React, { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Pull-to-refresh component.
 *
 * IMPORTANT: this wrapper does NOT set overflow-y or overscroll-behavior
 * on its root div — the page scroll must remain on the document/window,
 * otherwise mobile Android WebView stops scrolling entirely.
 *
 * Pull gesture:
 * - Uses window.scrollY to detect if the page is at the top.
 * - Only starts pulling if scrollY is already <= SCROLL_TOLERANCE.
 * - Indicator appears via absolute-positioned div (does not push content).
 */
const PullToRefresh = React.forwardRef(({ onRefresh, children, className = '', onScroll }, ref) => {
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const THRESHOLD = 70;
  const MAX_PULL = 140;
  const SCROLL_TOLERANCE = 30;

  const getScrollTop = () => (window.scrollY || document.documentElement.scrollTop || 0);

  const handleTouchStart = useCallback((e) => {
    if (getScrollTop() > SCROLL_TOLERANCE) return;
    startY.current = e.touches[0].clientY;
    setPulling(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!pulling || refreshing) return;
    if (getScrollTop() > SCROLL_TOLERANCE) {
      setPullDistance(0);
      setPulling(false);
      return;
    }
    const diff = e.touches[0].clientY - startY.current;
    if (diff > 0) {
      setPullDistance(Math.min(diff * 0.5, MAX_PULL));
    }
  }, [pulling, refreshing]);

  const handleTouchEnd = useCallback(async () => {
    if (!pulling) return;
    setPulling(false);
    if (pullDistance >= THRESHOLD && !refreshing && onRefresh) {
      setRefreshing(true);
      try {
        await onRefresh();
      } catch {}
      setTimeout(() => setRefreshing(false), 250);
    }
    setPullDistance(0);
  }, [pulling, pullDistance, refreshing, onRefresh]);

  useEffect(() => {
    const ts = (e) => handleTouchStart(e);
    const tm = (e) => handleTouchMove(e);
    const te = () => handleTouchEnd();
    window.addEventListener('touchstart', ts, { passive: true });
    window.addEventListener('touchmove', tm, { passive: true });
    window.addEventListener('touchend', te, { passive: true });
    window.addEventListener('touchcancel', te, { passive: true });
    return () => {
      window.removeEventListener('touchstart', ts);
      window.removeEventListener('touchmove', tm);
      window.removeEventListener('touchend', te);
      window.removeEventListener('touchcancel', te);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <div ref={ref} className={className} style={{ position: 'relative' }} onScroll={onScroll}>
      {(pullDistance > 0 || refreshing) && (
        <div
          className="absolute left-0 right-0 flex items-center justify-center pointer-events-none z-50"
          style={{
            top: 0,
            height: refreshing ? 44 : Math.max(pullDistance, 0),
            transition: refreshing ? 'height 200ms' : 'none',
          }}
        >
          <div
            className={`w-5 h-5 border-2 border-[#F5A623] border-t-transparent rounded-full ${
              refreshing || pullDistance >= THRESHOLD ? 'animate-spin' : ''
            }`}
            style={{
              opacity: refreshing ? 1 : Math.min(pullDistance / THRESHOLD, 1),
              transform: !refreshing && pullDistance < THRESHOLD ? `rotate(${pullDistance * 3}deg)` : undefined,
            }}
          />
        </div>
      )}
      {children}
    </div>
  );
});

PullToRefresh.displayName = 'PullToRefresh';

export default PullToRefresh;
